import { useSelector } from 'react-redux';
import AddIcon from '@material-ui/icons/Add';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import DictionaryManagementDialog from '../common/DictionaryManagementDialog';
import { VEHICLE_DICTIONARIES } from '../../constants/vehicleDictionaryConstants';
import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import { RMSTextField, RMSSelect, RMSCheckbox } from 'features/shared/components/form-controls';
import { SectionCard } from 'features/shared/components/cards';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { getDictionarySelectProps } from 'features/shared/utility/dictionarySelectHelper';

const VehicleDetailsForm = ({ canEdit = true }) => {
  const { control, setValue } = useFormContext();
  const [dictionaryTarget, setDictionaryTarget] = useState(null);
  const vehiclePermissions = usePermissions(PERMISSION_RESOURCES.VEHICLE);
  const canEditVehicle = canEdit && vehiclePermissions.Edit;
  const makePermissions = useSelector(
    (state) => state.permissions?.dictionary?.[VEHICLE_DICTIONARIES.MAKE.name]
  );
  const stylePermissions = useSelector(
    (state) => state.permissions?.dictionary?.[VEHICLE_DICTIONARIES.STYLE.name]
  );
  const showMakeAddIcon =
    canEditVehicle &&
    Boolean(makePermissions?.Create || makePermissions?.Edit || makePermissions?.Delete);
  const showStyleAddIcon =
    canEditVehicle &&
    Boolean(stylePermissions?.Create || stylePermissions?.Edit || stylePermissions?.Delete);
  const [Make, GarageIndicator] = useWatch({
    control,
    name: ['Make', 'GarageIndicator'],
  });

  const {
    vehicleMakeOptions,
    vehicleMakesModelsOptions,
    vehicleStyleOptions,
    vehicleColorOptions,
    vehicleClassificationOptions,
    vehicleBrandOptions,
    vehicleBranderOptions,
    vehiclePropertyOptions,
    vehicleCommercialOptions,
    transmissionOptions,
    garageFacilityOptions,
  } = useUserDictionaryOptions();

  const modelOptions = useMemo(() => {
    if (!Make) return [];
    return vehicleMakesModelsOptions
      .filter((mm) => mm.MakeCode === Make)
      .map((mm) => ({ Code: mm.ModelCode, Description: mm.ModelDescription }));
  }, [Make, vehicleMakesModelsOptions]);

  const handleMakeChange = useCallback(() => setValue('Model', ''), [setValue]);

  const handleVinChange = useCallback(
    (value) =>
      setValue('VIN', (value || '').toUpperCase(), { shouldDirty: true, shouldValidate: true }),
    [setValue]
  );

  return (
    <>
      {' '}
      <SectionCard label="Details">
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <RMSTextField
              name="Year"
              label="Year"
              type="number"
              inputProps={{ min: 0, max: 2100 }}
            />
          </Grid>
          <Grid item xs={3}>
            <RMSSelect
              name="Make"
              label="Make"
              options={vehicleMakeOptions}
              onChange={handleMakeChange}
              prefixIcon={showMakeAddIcon ? <AddIcon fontSize="small" /> : undefined}
              onPrefixIconClick={
                showMakeAddIcon ? () => setDictionaryTarget(VEHICLE_DICTIONARIES.MAKE) : undefined
              }
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={3}>
            <RMSSelect
              name="Model"
              label="Model"
              options={modelOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={3}>
            <RMSSelect
              name="Style"
              label="Style"
              options={vehicleStyleOptions}
              prefixIcon={showStyleAddIcon ? <AddIcon fontSize="small" /> : undefined}
              onPrefixIconClick={
                showStyleAddIcon ? () => setDictionaryTarget(VEHICLE_DICTIONARIES.STYLE) : undefined
              }
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={3}>
            <RMSTextField
              name="DoorQuantity"
              label="Doors"
              type="number"
              inputProps={{ min: 0, max: 20 }}
            />
          </Grid>
          <Grid item xs={3}>
            <RMSTextField
              name="PassengerSafeQty"
              label="Passenger Quantity"
              type="number"
              inputProps={{ min: 0, max: 1000 }}
            />
          </Grid>
          <Grid item xs={3}>
            <RMSTextField
              name="SeatQty"
              label="Seat Quantity"
              type="number"
              inputProps={{ min: 0, max: 1000 }}
            />
          </Grid>
          <Grid item xs={3}>
            <RMSSelect
              name="Classification"
              label="Classification"
              options={vehicleClassificationOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={4}>
            <RMSSelect
              name="PrimaryColor"
              label="Primary Color"
              options={vehicleColorOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={4}>
            <RMSSelect
              name="SecondaryColor"
              label="Secondary Color"
              options={vehicleColorOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={4}>
            <RMSSelect
              name="InteriorColor"
              label="Interior Color"
              options={vehicleColorOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Brand"
              label="Brand"
              options={vehicleBrandOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Brander"
              label="Brander"
              options={vehicleBranderOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={12}>
            <RMSTextField
              name="Description"
              label="Description"
              multiline
              rows={2}
              inputProps={{ maxLength: 8000 }}
            />
          </Grid>
        </Grid>
      </SectionCard>
      <SectionCard label="Credentials and Analysis">
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <RMSTextField
              name="VIN"
              label="VIN"
              inputProps={{ maxLength: 17 }}
              onChange={handleVinChange}
            />
          </Grid>
          <Grid item xs={4}>
            <RMSTextField
              name="VINAnalysisText"
              label="VIN Analysis Text"
              inputProps={{ maxLength: 2000 }}
            />
          </Grid>
          <Grid item xs={4}>
            <RMSSelect
              name="Transmission"
              label="Transmission Code"
              options={transmissionOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={4}>
            <RMSTextField name="PropertyID" label="Property ID" inputProps={{ maxLength: 25 }} />
          </Grid>
          <Grid item xs={4}>
            <RMSSelect
              name="Property"
              label="Property Code"
              options={vehiclePropertyOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={4}>
            <RMSTextField name="EmissionInspectionQty" label="Emission Inspection" type="number" />
          </Grid>
          <Grid item xs={4}>
            <RMSTextField
              name="CommercialID"
              label="Commercial ID"
              inputProps={{ maxLength: 25 }}
            />
          </Grid>
          <Grid item xs={4}>
            <RMSSelect
              name="Commercial"
              label="Commercial Code"
              options={vehicleCommercialOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={4}>
            <RMSCheckbox name="IsCMVIndicator" label="CMV" />
          </Grid>
        </Grid>
      </SectionCard>
      <SectionCard label="Flags">
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <RMSCheckbox name="IsRented" label="Rented" />
          </Grid>
          <Grid item xs={2}>
            <RMSCheckbox name="IsWanted" label="Wanted" />
          </Grid>
          <Grid item xs={3}>
            <RMSCheckbox name="GarageIndicator" label="Garage Indicator" />
          </Grid>
          <Grid item xs={5}>
            <RMSSelect
              name="GarageFacility"
              label="Garage Facility"
              options={garageFacilityOptions}
              disabled={!GarageIndicator}
              {...getDictionarySelectProps()}
            />
          </Grid>
        </Grid>
      </SectionCard>
      {dictionaryTarget && (
        <DictionaryManagementDialog
          open
          dictionary={dictionaryTarget.name}
          title={dictionaryTarget.title}
          onClose={() => setDictionaryTarget(null)}
        />
      )}
    </>
  );
};
export default VehicleDetailsForm;
